/**
 * PurchaseOrderQuickCreateDialog - Minimal dialog for quick PO creation
 *
 * Fields: vendor_id (required), expected_delivery_date, notes
 * Redirects to detail page after creation.
 */
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { Package, Warehouse } from 'lucide-react';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { BidiText, CurrencyPicker, DialogForm, DatePicker } from '../common';
import { CompanyCreateField, useCompanyCreatePrecedence } from '../multi-company';
import { useApiError } from '../../hooks/useApiError';
import { useDefaultCurrency } from '../../hooks/useDefaultCurrency';
import { navigateToEntity } from '../../utils/entityUrls';
import { useCreatePurchaseOrder } from '../../procurement/hooks/usePurchaseOrders';
import { useSelectableVendors, useVendor } from '../../procurement/hooks/useVendors';
import { partyFunctionLabel } from '../parties';
import { VendorPicker } from '../../procurement/components/VendorPicker';
import { toDateOnlyString } from '@/shared/utils/dateFormat';

/**
 * Seed line carried from an A→B context (e.g. a reorder alert) so the PO opens
 * with the product + suggested qty already on it instead of "No items".
 */
export interface PurchaseOrderPrefillLine {
  offeringId: string;
  variantId: string | null;
  quantity: number;
  productName: string | null;
  sku: string | null;
}

interface PurchaseOrderQuickCreateDialogProps {
  open: boolean;
  onClose: () => void;
  defaultVendorId?: string;
  /** Pre-seed a single PO line (offering + qty). Omit for the header-only path. */
  prefillLine?: PurchaseOrderPrefillLine;
  /**
   * Pre-set the deliver-to (receiving) warehouse. Intentionally independent of
   * prefillLine: an alert whose product has no offering handle still seeds the
   * warehouse on an otherwise header-only PO.
   */
  defaultWarehouseId?: string;
  /** Display name for {@link defaultWarehouseId} in the read-only summary. */
  warehouseName?: string | null;
}

interface QuickCreateFormData {
  vendor_id: string;
  company_id: string | null;
  currency_code: string;
  expected_delivery_date: Date | undefined;
  notes: string;
}

export function PurchaseOrderQuickCreateDialog({
  open,
  onClose,
  defaultVendorId,
  prefillLine,
  defaultWarehouseId,
  warehouseName,
}: PurchaseOrderQuickCreateDialogProps) {
  const { t } = useTranslation(['procurement', 'common', 'shipments']);
  const navigate = useNavigate();
  const createMutation = useCreatePurchaseOrder();
  const { handleMutationError } = useApiError();
  const { data: vendors } = useSelectableVendors();
  const companyPrecedence = useCompanyCreatePrecedence();
  const { defaultCompanyId } = companyPrecedence;
  const defaultCurrency = useDefaultCurrency();

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<QuickCreateFormData>({
    mode: 'onBlur',
    defaultValues: {
      vendor_id: defaultVendorId || '',
      company_id: null,
      // Seeded from the single frontend seam, not a literal — an ILS tenant must
      // not open this dialog on USD (ERP § P13 at creation).
      currency_code: defaultCurrency,
      expected_delivery_date: undefined,
      notes: '',
    },
  });

  useEffect(() => {
    if (open) {
      reset({
        vendor_id: defaultVendorId || '',
        // Company precedence: pre-fill the specific global scope; null (= force
        // pick) when scope is "All companies".
        company_id: defaultCompanyId || null,
        currency_code: defaultCurrency,
        expected_delivery_date: undefined,
        notes: '',
      });
    }
  }, [open, defaultVendorId, defaultCompanyId, defaultCurrency, reset]);

  // Foreign-currency import POs: default the currency to the chosen supplier's
  // currency so a EUR/GBP vendor drafts a PO in their currency (overridable).
  const selectedVendorId = useWatch({ control, name: 'vendor_id' });
  useEffect(() => {
    const vendor = vendors?.find((v) => v.organization_id === selectedVendorId);
    if (vendor?.currency_code) {
      setValue('currency_code', vendor.currency_code);
    }
  }, [selectedVendorId, vendors, setValue]);
  // Partner functions (D2): the order will start from the vendor relationship's
  // default parties. Shown here so the buyer knows who will be on the order;
  // edited on the order itself, where the Parties card lives.
  const selectedVendorProfileId =
    vendors?.find((v) => v.organization_id === selectedVendorId)?.vendor_profile_id ?? '';
  const { data: selectedVendor } = useVendor(selectedVendorProfileId);
  const defaultParties = selectedVendor?.default_parties ?? [];

  const onSubmit = async (data: QuickCreateFormData) => {
    // Precedence rule 2's acknowledgement. NOT in the `validate` above:
    // react-hook-form caches a field error until that field re-validates, and
    // confirming the chip changes no value, so the message would outlive the
    // condition. Here it is the field's own, and `confirm()` clears it.
    if (companyPrecedence.blockCreate(data.company_id ?? '')) return;
    try {
      const po = await createMutation.mutateAsync({
        vendor_id: data.vendor_id,
        company_id: data.company_id || undefined,
        currency_code: data.currency_code || undefined,
        expected_delivery_date: data.expected_delivery_date
          ? toDateOnlyString(data.expected_delivery_date)
          : undefined,
        notes: data.notes || undefined,
        // A→B seed (e.g. reorder alert): carry the deliver-to warehouse and one
        // line for the alert's product at the suggested qty. unit_cost is 0 here
        // (matches "Add line"); the clerk sets cost on the detail page.
        warehouse_id: defaultWarehouseId || undefined,
        items: prefillLine
          ? [
              {
                offering_id: prefillLine.offeringId,
                variant_id: prefillLine.variantId,
                quantity: prefillLine.quantity,
                unit_cost: 0,
              },
            ]
          : undefined,
      });
      onClose();
      navigateToEntity(navigate, 'proc_purchase_orders', po.id);
    } catch (error) {
      handleMutationError(error, setError);
    }
  };

  return (
    <DialogForm
      open={open}
      onClose={onClose}
      title={t('purchaseOrders.create')}
      onSubmit={handleSubmit(onSubmit)}
      submitLabel={t('common:app.create')}
      isLoading={isSubmitting}
      maxWidth="sm"
    >
      <div className="space-y-4">
        {prefillLine && (
          <div className="space-y-2 rounded-md border border-border bg-muted/40 p-3">
            <div className="flex items-center gap-2">
              <Package className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="truncate font-medium">
                {prefillLine.productName ?? prefillLine.offeringId}
              </span>
              {prefillLine.sku && (
                <span className="truncate text-xs text-muted-foreground">
                  {prefillLine.sku}
                </span>
              )}
              <span className="ms-auto shrink-0 text-sm font-medium">
                {t('purchaseOrders.reorderQty', { count: prefillLine.quantity })}
              </span>
            </div>
            {warehouseName && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Warehouse className="h-4 w-4 shrink-0" />
                <span>{t('purchaseOrders.deliverTo')}:</span>
                <span className="truncate text-foreground">{warehouseName}</span>
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              {t('purchaseOrders.reorderSeedHint')}
            </p>
          </div>
        )}
        <Controller
          name="company_id"
          control={control}
          rules={{
            validate: (v) =>
              !companyPrecedence.multiCompany || !!v || t('common:validation.required'),
          }}
          render={({ field }) => (
            <CompanyCreateField
              precedence={companyPrecedence}
              value={field.value ?? ''}
              onChange={(id) => field.onChange(id || null)}
              error={!!errors.company_id}
              errorMessage={errors.company_id?.message}
            />
          )}
        />
        <div className="space-y-2">
          <Label required>{t('purchaseOrders.vendor')}</Label>
          <Controller
            name="vendor_id"
            control={control}
            rules={{ required: t('common:validation.required') }}
            render={({ field }) => (
              // PO vendor_id is the organization id, not the vendor-profile PK —
              // VendorPicker's value semantics match (it emits organization_id).
              <VendorPicker
                value={field.value}
                onChange={field.onChange}
                required
              />
            )}
          />
          {errors.vendor_id && (
            <p className="text-sm text-destructive">{errors.vendor_id.message}</p>
          )}
        </div>

        <Controller
          name="currency_code"
          control={control}
          render={({ field }) => (
            <CurrencyPicker
              value={field.value}
              onChange={(code) => field.onChange(code || defaultCurrency)}
              label={t('purchaseOrders.currency')}
              helperText={t('purchaseOrders.currencyVendorHint')}
            />
          )}
        />

        <div className="space-y-2">
          <Label>{t('purchaseOrders.expectedDeliveryDate')}</Label>
          <Controller
            name="expected_delivery_date"
            control={control}
            render={({ field }) => (
              <DatePicker
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="notes">{t('purchaseOrders.notes')}</Label>
          <Controller
            name="notes"
            control={control}
            render={({ field }) => (
              <Textarea
                {...field}
                id="notes"
                placeholder={t('purchaseOrders.notesPlaceholder')}
                rows={3}
              />
            )}
          />
        </div>

        {selectedVendorProfileId && (
          <div className="space-y-1 rounded-md border border-border bg-muted/40 p-3">
            <p className="text-xs font-medium text-muted-foreground">
              {t('purchaseOrders.parties.defaultsPreview')}
            </p>
            {defaultParties.length === 0 ? (
              <p className="text-xs text-muted-foreground">{t('purchaseOrders.parties.noDefaults')}</p>
            ) : (
              <ul className="space-y-1 text-sm">
                {defaultParties.map((party) => (
                  <li key={party.id} className="flex flex-wrap items-baseline gap-x-2">
                    <span className="text-muted-foreground">
                      {partyFunctionLabel(t, party.party_function)}
                    </span>
                    <BidiText className="font-medium">
                      {party.organization_name ?? party.party_name ?? ''}
                    </BidiText>
                  </li>
                ))}
              </ul>
            )}
            <p className="text-xs text-muted-foreground">{t('purchaseOrders.parties.defaultsPreviewHint')}</p>
          </div>
        )}

        {!prefillLine && (
          <p className="text-xs text-muted-foreground">
            {t('purchaseOrders.quickCreateHint')}
          </p>
        )}
      </div>
    </DialogForm>
  );
}
